import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";

export interface InvitationData {
  first_name: string;
  last_name: string;
  email: string;
  role:
    | "role:platform:admin"
    | "role:org:admin"
    | "role:facility:manager"
    | "role:staff:medical"
    | "role:staff:care";
  facility_id?: string;
  group_home_id?: string;
  position: string;
  phone?: string;
  hire_date?: string;
  send_invitation?: boolean;
}

export interface InvitationResponse {
  message: string;
  email_queued: boolean;
  invitation: {
    id: string;
    email: string;
    name: string;
    expires_at: string;
    invitation_link: string;
  };
}

export interface Invitation {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  position: string;
  invitation_token: string;
  invitation_sent_at: string;
  invitation_expires_at: string;
  invitation_details: {
    role: string;
    facility_id?: string | null;
    group_home_id?: string | null;
  } | null;
  user_id: string | null;
  invited_by_employee: {
    first_name: string;
    last_name: string;
    position: string;
  } | null;
}

/**
 * Fetch invitations query
 * @param status - Filter by invitation status: 'pending', 'accepted', 'expired', 'all'
 */
export function useInvitations(
  status: "pending" | "accepted" | "expired" | "all" = "pending",
) {
  return useQuery({
    queryKey: ["invitations", status],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("status", status);

      const { data } = await api.get(`/invitations?${params.toString()}`);
      return data.data as Invitation[];
    },
  });
}

/**
 * Send invitation mutation
 */
export function useSendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InvitationData) => {
      const { data: response } = await api.post("/invitations", data);
      return response.data as InvitationResponse;
    },
    // US-081: InviteEmployeeModal owns error UX via a bespoke catch (field
    // errors + toast). Suppressed so the global handler stays silent.
    meta: { suppressErrorNotification: true },
    onSuccess: () => {
      // Refresh employees list and invitations to show new pending invitation
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}

/**
 * Send a group-home invitation as a platform admin.
 *
 * Posts to the admin-scoped /api/admin/group-homes/:id/invitations endpoint.
 * The portal useSendInvitation() (POST /api/invitations) 403s platform users
 * ("restricted to /admin routes"), so admin onboarding flows use this instead.
 */
export function useSendGroupHomeInvitation(groupHomeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InvitationData) => {
      const { data: response } = await api.post(
        `/admin/group-homes/${groupHomeId}/invitations`,
        data,
      );
      return response.data as InvitationResponse;
    },
    // US-081: InviteGroupHomeUserModal owns error UX via a bespoke catch toast.
    // Suppressed so the global handler stays silent.
    meta: { suppressErrorNotification: true },
    onSuccess: () => {
      // The group-home detail page reads useGroupHomeInvitations, which is
      // keyed [GROUP_HOME_INVITATIONS_KEY, groupHomeId, status] — NOT
      // ["invitations"] (that key belongs to the portal hook, which this page
      // never mounts). Invalidating only the portal key left the Invitations
      // panel reading "No pending invitations" after a successful invite,
      // telling the operator by absence that the write had failed. Resend and
      // delete below always killed the right key; send was the odd one out.
      queryClient.invalidateQueries({
        queryKey: [GROUP_HOME_INVITATIONS_KEY, groupHomeId],
      });
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

/**
 * Send a platform-level invitation as a platform admin (platform admin /
 * platform manager / auditor roles).
 *
 * Posts to the admin-scoped /api/admin/invitations endpoint. The portal
 * useSendInvitation() (POST /api/invitations) 403s platform users.
 */
export function useSendPlatformInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InvitationData) => {
      const { data: response } = await api.post("/admin/invitations", data);
      return response.data as InvitationResponse;
    },
    // US-081: InvitePlatformUserModal owns error UX via a bespoke catch toast.
    // Suppressed so the global handler stays silent.
    meta: { suppressErrorNotification: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}

/**
 * Resend invitation mutation
 */
export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; InvitationsTab/AuditorManagerInvitationsTab catch is sole caller.
    meta: { errorFallback: "Failed to resend invitation" },
    mutationFn: async (invitationId: string) => {
      const { data: response } = await api.post(
        `/invitations/resend/${invitationId}`,
      );
      return response.data as InvitationResponse;
    },
    onSuccess: () => {
      // Refresh invitations list to show updated sent_at timestamp
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}

/**
 * Delete invitation mutation
 */
export function useDeleteInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; InvitationsTab/AuditorManagerInvitationsTab catch is sole caller.
    meta: { errorFallback: "Failed to delete invitation" },
    mutationFn: async (invitationId: string) => {
      await api.delete(`/invitations/delete/${invitationId}`);
    },
    onSuccess: () => {
      // Refresh invitations list and employees list
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

// ── Group-home invitation management (platform admin) ────────────────────────
// Lists/resends/deletes invitations for ONE group home, via the admin-scoped
// /api/admin/invitations* endpoints. Used by the platform-admin group-home
// detail page.

const GROUP_HOME_INVITATIONS_KEY = "group-home-invitations";

export function useGroupHomeInvitations(
  groupHomeId: string,
  status: "pending" | "accepted" | "expired" | "all" = "pending",
) {
  return useQuery({
    queryKey: [GROUP_HOME_INVITATIONS_KEY, groupHomeId, status],
    queryFn: async () => {
      const params = new URLSearchParams({ status, groupHomeId });
      const { data } = await api.get(`/admin/invitations?${params.toString()}`);
      return data.data as Invitation[];
    },
    enabled: !!groupHomeId,
  });
}

export function useResendGroupHomeInvitation(groupHomeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // US-081: error fallback centralized here; GroupHomeInvitationsSection catch is sole caller.
    meta: { errorFallback: "Failed to resend invitation" },
    mutationFn: async (invitationId: string) => {
      const { data: response } = await api.post(
        `/admin/invitations/${invitationId}/resend`,
      );
      return response.data as InvitationResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GROUP_HOME_INVITATIONS_KEY, groupHomeId],
      });
    },
  });
}

export function useDeleteGroupHomeInvitation(groupHomeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // US-081: error fallback centralized here; GroupHomeInvitationsSection catch is sole caller.
    meta: { errorFallback: "Failed to delete invitation" },
    mutationFn: async (invitationId: string) => {
      await api.delete(`/admin/invitations/${invitationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GROUP_HOME_INVITATIONS_KEY, groupHomeId],
      });
      // user count / list on the group home detail page may change
      queryClient.invalidateQueries({ queryKey: ["admin", "group-homes"] });
    },
  });
}
